import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ChatFile, { CLIP, Paperclip, isAutoBody, isTicketFileNote } from './ChatFile';
import { ReactionSheet, Smiley } from './EmojiPicker';
import { isJumbo, whoReacted } from '../lib/emoji';
import { initials, hueOf } from '../lib/initials';
import { splitMentions } from '../lib/mentions';
import Avatar from './Avatar';
import {
  BranchIcon, BranchLine, DeleteConfirm, EditBox, MessageMenu, MoreIcon, MovedChip, MovedFromNote, Versions
} from './ChatBranch';

/**
 * How a conversation READS on the chat screen: the messages, the day they
 * were said on, where the unread ones start, and keeping the newest in view.
 *
 * Gordon, 16 October, after using it for a week: make it "feel more like
 * Slack". Nothing he asked for was a new thing the server does - it was that
 * three lines from Marco in a minute arrived as three separate boxes, each
 * with MARCO VELLA over it, and a conversation that ran across two days had
 * nothing to say where one ended. So this file is about reading, and it holds
 * no data of its own: the page hands it the messages and it draws them.
 *
 * Styled from pages/ChatPage.css, `.ch-` like the rest of that screen,
 * because it is only ever drawn there.
 */

/** Lines from one person closer together than this read as one thought. */
export const GROUP_GAP_MS = 5 * 60 * 1000;

const asDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "14:05". The day is said once, above, so a line only needs the time. */
export const clockOf = (iso) => {
  const d = asDate(iso);
  return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
};

/** The full date and time, for the tooltip on a time. */
const fullOf = (iso) => {
  const d = asDate(iso);
  return d
    ? d.toLocaleString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
    : undefined;
};

/** A calendar day in local time, as a key: two messages share one or not. */
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * "Today", "Yesterday", or "Monday 12 October" - and the year as well once it
 * is not this one. Built from parts rather than taken whole from
 * toLocaleDateString, because whether en-GB puts a comma after the weekday
 * has changed between browser versions, and a test that pins the words should
 * not depend on which Chrome is installed.
 */
export const dayLabel = (d, now = new Date()) => {
  if (dayKey(d) === dayKey(now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';
  const opts = { weekday: 'long', day: 'numeric', month: 'long' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', opts)
    .formatToParts(d).map((p) => [p.type, p.value]));
  return [parts.weekday, parts.day, parts.month, parts.year].filter(Boolean).join(' ');
};

// The letters and the tint in a circle: lib/initials.js, re-exported so
// everything that imports them from here is unchanged.
export { initials, hueOf };

/* -------------------------------------------------------------- links */

/*
 * A web address in a message becomes something you can click - Gordon pastes
 * supplier links and portal pages into chat all day, and copying one out of a
 * line of text on a phone is miserable.
 *
 * ONLY http and https, and only ever as React elements: the text is never
 * handed to the browser as HTML. "javascript:alert(1)" typed into a message
 * is words, and stays words, because nothing here could turn it into anything
 * else. The address is also read back through URL() before it is linked, so
 * something that merely starts "http" and is not an address stays text.
 */
const LINK = /\bhttps?:\/\/[^\s<>"]+/gi;

/** Full stops and brackets a sentence put after an address are not part of it. */
const trimTail = (url) => {
  let out = url;
  for (;;) {
    const last = out.slice(-1);
    if (/[.,;:!?'"]/.test(last)) { out = out.slice(0, -1); continue; }
    // A closing bracket belongs to the address only if it opened one too, as
    // Wikipedia's do: "(see https://x.org/a_(b))".
    if (last === ')' && (out.match(/\(/g) || []).length < (out.match(/\)/g) || []).length) {
      out = out.slice(0, -1);
      continue;
    }
    return out;
  }
};

const safeHref = (text) => {
  try {
    const u = new URL(text);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : null;
  } catch {
    return null;
  }
};

export const linkify = (text) => {
  const s = String(text ?? '');
  const out = [];
  let at = 0;
  for (const m of s.matchAll(LINK)) {
    const url = trimTail(m[0]);
    const href = safeHref(url);
    if (!href) continue;
    if (m.index > at) out.push(s.slice(at, m.index));
    out.push(
      <a key={`${m.index}`} className="ch-link" href={href} target="_blank"
        rel="noopener noreferrer">
        {url}
      </a>
    );
    at = m.index + url.length;
  }
  if (at < s.length) out.push(s.slice(at));
  return out;
};

/* ------------------------------------------------------- the timeline */

/** Whose line it is, for grouping: me, or whoever the server says. */
const whoOf = (m) => (m.mine ? 'me' : (m.authorKey || m.author || '?'));

/**
 * The messages, as the rows the list draws: day lines, the "New messages"
 * line, notes, and messages marked with whether they start a group.
 *
 * A group is Slack's: consecutive lines from one person, each within five
 * minutes of the one before, under one name and one picture. Anything that is
 * not one of their lines breaks it - a day line, the unread line, a "Gordon
 * added Marco" note, or a file going onto a ticket, which is a record of
 * something done rather than part of what was being said.
 */
export const buildTimeline = (messages, unreadKey, divider = null) => {
  const rows = [];
  let day = null;
  let prev = null;     // { who, at } of the line before, while a group is open
  for (const m of messages || []) {
    const at = asDate(m.at);
    if (at && dayKey(at) !== day) {
      day = dayKey(at);
      rows.push({ type: 'day', key: `day-${day}`, label: dayLabel(at) });
      prev = null;
    }
    if (unreadKey != null && m.key === unreadKey) {
      rows.push({ type: 'unread', key: 'unread-line' });
      prev = null;
    }
    if (m.kind === 'event') {
      rows.push({ type: 'note', key: m.key, m });
      prev = null;
      continue;
    }
    const fileNote = (m.files || []).length === 0 && isTicketFileNote(m.body);
    const who = whoOf(m);
    const start = fileNote || !prev || prev.who !== who || !at || !prev.at
      || at - prev.at > GROUP_GAP_MS;
    rows.push({ type: 'msg', key: m.key, m, start, fileNote });
    prev = fileNote ? null : { who, at };
    // A branch's panel: the parent, then a line saying how many replies are
    // under it, then the replies - which start a fresh group of their own.
    if (divider && m.key === divider.after) {
      rows.push({ type: 'divider', key: 'branch-divider', label: divider.label });
      prev = null;
    }
  }
  return rows;
};

/**
 * Where the "New messages" line goes: before the Nth message from the end
 * that somebody else wrote, N being the unread count the list showed when the
 * conversation was opened. My own lines are skipped, because the server never
 * counts them as unread - I wrote them.
 */
export const firstUnreadKey = (messages, count) => {
  if (!count || count <= 0 || !messages || messages.length === 0) return null;
  let seen = 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].mine) continue;
    seen += 1;
    if (seen === count) return messages[i].key;
  }
  // More unread than are loaded: they all are.
  const firstOther = messages.find((m) => !m.mine);
  return firstOther ? firstOther.key : null;
};

/** What a message says: its words, its files, or a note about a file. */
/**
 * A message's words, with its @mentions drawn as chips (084) - lit when it
 * names me - and its web addresses as links. Both only ever as React
 * elements, never as HTML.
 */
export const richText = (body, mentions, meKey) => splitMentions(body, mentions).flatMap((part, i) => (part.mention
  ? [(
    <span key={`m${i}`} className="ch-mention"
      data-me={part.mention.key === meKey || part.mention.key === 'everyone' || undefined}>
      {part.text}
    </span>
  )]
  : linkify(part.text).map((x, j) => (typeof x === 'string' ? x : React.cloneElement(x, { key: `l${i}-${j}` })))));

const Said = ({ m, fileNote, onFileError, react }) => {
  const files = m.files || [];
  // Deleted (084): the words and the files are gone; what is left says so.
  if (m.deleted) {
    return <span className="ch-msg-deleted">This message was deleted</span>;
  }
  if (fileNote) {
    // Not a download: the file is in the ticket's Files, which "Open ticket"
    // above leads to. This line only records that it went there.
    return (
      <span className="ch-file-note">
        <Paperclip />
        <span>{m.body.slice(CLIP.length + 1)}</span>
      </span>
    );
  }
  // A message that is one to three emoji and nothing else is drawn large,
  // as WhatsApp and Slack draw it: "👍" is the whole answer, not a footnote.
  const jumbo = files.length === 0 && isJumbo(m.body);
  return (
    <>
      {m.body && !isAutoBody(m.body, files) && (
        <span className="ch-msg-body" data-jumbo={jumbo || undefined}>
          {Array.isArray(m.mentions) && m.mentions.length ? richText(m.body, m.mentions, react?.meKey) : linkify(m.body)}
        </span>
      )}
      {m.edited && (
        <button type="button" className="ch-edited" aria-haspopup="dialog"
          title="Edited - show the earlier versions"
          onClick={(e) => react?.onVersions && react.onVersions(m, e.currentTarget)}>
          (edited)
        </button>
      )}
      {files.length > 0 && (
        <span className="ch-files">
          {files.map((f) => <ChatFile key={f.id} file={f} onError={onFileError} />)}
        </span>
      )}
    </>
  );
};

/* ---------------------------------------------------------- reactions */

/*
 * REACTIONS, as Slack, WhatsApp and Teams have them. Gordon asked for emoji
 * "in a similar fashion" to those three, and in all three the commonest
 * thing anybody does with an emoji is not write it but put it under somebody
 * else's message: a 👍 that says "seen, fine" without a line in the
 * conversation that says so.
 *
 * Under the bubble, one chip per emoji with how many, mine marked (and
 * pressed, to a screen reader), and the names in its tooltip. Clicking a
 * chip adds me to it or takes me off. A "+" at the end opens the picker.
 *
 * Every message that has an id the server can find it by - a General line, a
 * group line, a ticket comment. Not the "Gordon added Marco" notes in a
 * group, which are something that happened rather than something said, and
 * are drawn by the timeline below without coming through here at all.
 */
export const canReact = (m, react) => Boolean(react && react.onReact && m && m.id && m.scope
  && m.kind !== 'event');

/*
 * "MAKE A TO-DO" (batch 4) is in the same popover and the same sheet: any
 * line the server can find by its id - direct, group or ticket - can become
 * a to-do, which keeps a link back to it. A note about something that
 * happened is not a line anybody said, and never reaches here.
 */
export const canTodo = (m, react) => Boolean(react && react.onTodo && m && m.id && m.scope
  && m.kind !== 'event' && !m.deleted);

/*
 * BRANCH OFF (084): a direct or group message somebody said - not a ticket's
 * comment, not a note about the group, not a deleted line - when the page
 * says branches are on.
 */
export const canBranch = (m, react) => Boolean(react && react.onBranch && m && m.id
  && (m.scope === 'direct' || m.scope === 'group') && m.kind !== 'event' && !m.deleted
  // 096: a branch moved to a topic takes no more replies - the topic does.
  && !m.moved);

/** Edit or delete, from the ⋯ beside the smiley: what the server said I may do. */
export const canChange = (m, react) => Boolean(react && (react.onEdit || react.onDelete) && m && m.id
  && !m.deleted && (m.mayEdit || m.mayDelete));

/** How long a finger has to stay on a message before it counts as a press. */
export const LONG_PRESS_MS = 450;

const Reactions = ({ m, react }) => {
  const list = (m.reactions || []).filter((r) => r && r.emoji && Number(r.count) > 0);
  if (list.length === 0) return null;
  return (
    <div className="ch-reactions" role="group" aria-label="Reactions">
      {list.map((r) => {
        const who = whoReacted(r, react.me);
        return (
          <button
            key={r.emoji}
            type="button"
            className="ch-reaction"
            aria-pressed={r.mine === true}
            title={who ? `${who} reacted with ${r.emoji}` : undefined}
            aria-label={`${r.emoji} ${r.count}${who ? `: ${who}` : ''}`}
            onClick={() => react.onReact(m, r.emoji)}
          >
            <span className="ch-reaction-emoji" aria-hidden="true">{r.emoji}</span>
            <span className="ch-reaction-count" aria-hidden="true">{r.count}</span>
          </button>
        );
      })}
      {!m.deleted && (
        <button
          type="button"
          className="ch-reaction ch-reaction-add"
          aria-label="Add a reaction"
          title="Add a reaction"
          onClick={(e) => react.onMore(m, e.currentTarget)}
        >
          <Smiley size={14} />
          <span aria-hidden="true">+</span>
        </button>
      )}
    </div>
  );
};

/*
 * THE WAY IN: a small, quiet smiley at the end of the message's text, after
 * its last line, as the phone has always had beside the bubble. Gordon, 21
 * October: "use a format similar to the mobile view when it comes to adding
 * emojis by having the small smiling face just next to the text instead of
 * the current row showing on the right side of the screen." It opens the
 * same six quick reactions the phone's long press brings up - as a popover
 * by the button on a desktop, a sheet on a phone - with "Make a to-do" in
 * it. Faint until the pointer is on the line or something in it has the
 * keyboard focus; always faintly there on a touch screen, where there is no
 * pointer to hover with (ChatPage.css). The bar that used to float over the
 * message's corner is gone: six emoji appearing at the far side of a wide
 * screen whenever the pointer crossed a line was the thing he counted.
 */
const Tap = ({ m, react, reactable }) => (
  <button
    type="button"
    className="ch-react-tap"
    aria-label={reactable ? 'React' : 'More'}
    title={reactable ? 'React to this message' : 'More'}
    aria-haspopup="dialog"
    onClick={(e) => react.onSheet(m, e.currentTarget)}
  >
    <Smiley size={15} />
  </button>
);

/*
 * A LONG PRESS, without spoiling the two things a finger on a message
 * normally does. Nothing is prevented when the finger lands, so the list
 * still scrolls and text can still be selected; moving more than a few
 * pixels is a scroll, and calls it off. Only once the press has counted is
 * the click that follows swallowed, so it does not also open a link that
 * happened to be under the finger.
 */
const usePress = (onPress) => {
  const timer = useRef(null);
  const from = useRef(null);
  const fired = useRef(false);
  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  if (!onPress) return {};
  return {
    onTouchStart: (e) => {
      const t = e.touches && e.touches[0];
      if (!t || e.touches.length > 1) { stop(); return; }
      fired.current = false;
      from.current = { x: t.clientX, y: t.clientY };
      stop();
      timer.current = setTimeout(() => {
        timer.current = null;
        fired.current = true;
        onPress();
      }, LONG_PRESS_MS);
    },
    onTouchMove: (e) => {
      const t = e.touches && e.touches[0];
      if (!t || !from.current) return;
      if (Math.abs(t.clientX - from.current.x) > 8 || Math.abs(t.clientY - from.current.y) > 8) stop();
    },
    onTouchEnd: (e) => {
      stop();
      if (fired.current && e.cancelable) e.preventDefault();
    },
    onTouchCancel: stop,
    onContextMenu: (e) => { if (fired.current) e.preventDefault(); }
  };
};

const Row = ({ row, onFileError, react, found }) => {
  const { m, start, fileNote } = row;
  const name = m.mine ? 'You' : m.author;
  const reactable = canReact(m, react) && !m.deleted;
  const todoable = canTodo(m, react);
  const branchable = canBranch(m, react);
  const changeable = canChange(m, react);
  const [menu, setMenu] = useState(null);
  const press = usePress(reactable || todoable || branchable || changeable ? () => react.onSheet(m, null) : null);
  const editing = Boolean(react?.editing && react.editing === m.id);
  /*
   * The time of a follow-on line is there, just quiet until you look for it:
   * on hover, or when something in the line has the keyboard focus. A screen
   * reader reads it either way - it is transparent, not hidden.
   */
  const peek = !start && (
    <time className="ch-msg-peek" dateTime={m.at || undefined} title={fullOf(m.at)}>
      {clockOf(m.at)}
    </time>
  );
  return (
    <div className="ch-msg" data-mine={m.mine || undefined} data-start={start || undefined}
      data-id={m.id || undefined} data-found={found || undefined}>
      {!m.mine && (
        <span className="ch-msg-gutter">
          {start
            ? (
              <Avatar person={{ key: m.authorKey, name: m.author }} className="ch-avatar ch-msg-avatar"
                data-hue={hueOf(m.authorKey || m.author)} aria-hidden="true" />
            )
            : peek}
        </span>
      )}
      {m.mine && peek}
      <div className="ch-msg-main">
        {start && (
          <div className="ch-msg-head">
            <span className="ch-msg-who">{name}</span>
            <time className="ch-msg-at" dateTime={m.at || undefined} title={fullOf(m.at)}>
              {clockOf(m.at)}
            </time>
          </div>
        )}
        {/* The copy that opened a topic (096) says where it came from. */}
        {m.movedFrom && (
          <MovedFromNote m={m} onOpen={react?.onOpenMoved ? () => react.onOpenMoved(m.movedFrom) : null} />
        )}
        {/* A reply also posted to the conversation (084) says what it
            answers, and opens the branch it is in. */}
        {m.replyTo && react?.onBranch && !react.inBranch && (
          <button type="button" className="ch-replied"
            onClick={() => react.onBranch({ id: m.replyTo.id, scope: m.scope }, { focus: m.id })}>
            <BranchIcon size={12} />
            <span className="ch-replied-text">
              Replied in a branch off {m.replyTo.author_name || 'a message'}: <q>{m.replyTo.snippet}</q>
            </span>
          </button>
        )}
        {editing ? (
          <EditBox m={m} react={react} />
        ) : (
          <div className="ch-bubble" data-deleted={m.deleted || undefined} {...press}>
            <Said m={m} fileNote={fileNote} onFileError={onFileError} react={react} />
            {/* After the last line of the text, inside the bubble, so it is
                "just next to the text" at every width - and Branch off and
                the ⋯ beside the smiley, never across the screen (084). */}
            {(reactable || todoable) && <Tap m={m} react={react} reactable={reactable} />}
            {branchable && (
              <button type="button" className="ch-msg-tool ch-branch-tap" aria-label="Branch off"
                title="Branch off - answer this message on its own"
                onClick={() => react.onBranch(m)}>
                <BranchIcon size={15} />
              </button>
            )}
            {changeable && (
              <button type="button" className="ch-msg-tool ch-more-tap" aria-label="More actions"
                title="Edit or delete" aria-haspopup="menu" aria-expanded={Boolean(menu)}
                onClick={(e) => setMenu(menu ? null : e.currentTarget)}>
                <MoreIcon size={15} />
              </button>
            )}
          </div>
        )}
        {menu && changeable && (
          <MessageMenu m={m} react={react} anchor={menu} onClose={() => setMenu(null)} />
        )}
        {(reactable || (m.deleted && (m.reactions || []).length > 0)) && <Reactions m={m} react={react} />}
        {m.branch && m.branch.count > 0 && react?.onBranch && !react.inBranch && (
          <BranchLine m={m} onOpen={() => react.onBranch(m)} />
        )}
        {/* 096: its branch became a topic - the chip opens it. */}
        {m.moved && react?.onOpenTopic && !react.inBranch && (
          <MovedChip m={m} onOpen={() => react.onOpenTopic(m.moved)} />
        )}
      </div>
    </div>
  );
};

const nearBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < 48;

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * The scrolling list of one conversation.
 *
 * `place` names the conversation, so the list knows when it has been swapped
 * for another one. `ready` says the messages are that conversation's own and
 * not the last one's still on screen. `unreadKey` is the message the "New
 * messages" line goes above - null for none, undefined while the page is
 * still working it out, in which case the list waits before choosing where to
 * land.
 *
 * SCROLLING, the way Slack does it. Opening a conversation lands on the
 * unread line if there is one, and at the bottom otherwise. After that, a new
 * line arriving keeps you at the bottom if that is where you were - and if you
 * had scrolled up to read something, it does NOT yank you away from it; a
 * "New messages" button appears instead and takes you down when you want.
 * Your own line always takes you down, because you have just written it.
 */
export function ChatTimeline({
  messages, place, ready, unreadKey, empty, onFileError, onReact, onMoreReactions, onMakeTodo, me,
  focusId = null, focusNonce = null,
  // 084: Branch off, edit and delete, @mentions, and a branch panel's divider.
  onBranch = null, onEdit = null, onDelete = null, meKey = null, editing = null, onEditSave = null,
  onEditCancel = null, mentionPeople = null, divider = null, inBranch = false,
  // 096: open the topic a branch was moved to; open the message a copy came from.
  onOpenTopic = null, onOpenMoved = null,
  // Older pages (wave 3): whether there are any, whether they are on their
  // way, and how to ask for them.
  hasOlder = false, loadingOlder = false, onLoadOlder = null
}) {
  const scroller = useRef(null);
  const pinned = useRef(true);
  const landed = useRef(null);
  const lastKey = useRef(null);
  /*
   * WHERE YOU WERE when older lines were asked for: the line then at the top
   * of the list and how far below the top of the pane it sat. When the older
   * page lands above it, the pane is scrolled by exactly as much as that line
   * moved, so what you were reading stays where it was rather than the list
   * jumping to the first of the new lines.
   */
  const anchor = useRef(null);
  const [jump, setJump] = useState(false);
  // The message whose quick reactions are up - { m, anchor }: the smiley
  // that opened them, which the popover sits by on a desktop, or null from
  // a long press, which a phone answers with a sheet anyway.
  const [sheetFor, setSheetFor] = useState(null);
  /*
   * A MESSAGE TO LAND ON, from search (079): `focusId` is its id. Once it is
   * in the list it is scrolled to the middle of the pane and lit for a moment,
   * instead of the unread line or the bottom - and only once per asking, so a
   * poll arriving afterwards does not drag you back to it. The page loads the
   * stretch down to it first when it is further back than the pages on
   * screen (ChatPage loadThrough, wave 3), so it lands once it arrives.
   */
  const [found, setFound] = useState(null);
  const focused = useRef(null);

  const rows = useMemo(() => buildTimeline(messages, unreadKey, divider), [messages, unreadKey, divider]);
  // The earlier wordings of an edited message, when somebody asks (084).
  const [versionsOf, setVersionsOf] = useState(null);
  // A delete asked for from a phone's long press, waiting for "Delete" again.
  const [confirmFor, setConfirmFor] = useState(null);

  useEffect(() => { setJump(false); setSheetFor(null); setVersionsOf(null); }, [place]);

  /*
   * What a message needs to be reacted to, handed down once. Null when the
   * page has not said how - the list then draws no reaction controls at all
   * rather than ones that do nothing.
   */
  const react = useMemo(() => (onReact || onMakeTodo || onBranch ? {
    me,
    meKey,
    onReact: onReact || null,
    onTodo: onMakeTodo || null,
    onMore: (m, anchor) => onMoreReactions && onMoreReactions(m, anchor),
    onSheet: (m, anchor) => setSheetFor({ m, anchor: anchor || null }),
    onBranch,
    onEdit,
    onDelete,
    onVersions: (m, anchor) => setVersionsOf({ m, anchor }),
    editing,
    onEditSave,
    onEditCancel,
    mentionPeople,
    inBranch,
    onOpenTopic,
    onOpenMoved
  } : null), [onReact, onMoreReactions, onMakeTodo, me, meKey, onBranch, onEdit, onDelete, editing,
    onEditSave, onEditCancel, mentionPeople, inBranch, onOpenTopic, onOpenMoved]);

  // Lit for a moment, then not: a highlight that stays is a second unread line.
  useEffect(() => {
    if (!found) return undefined;
    const t = setTimeout(() => setFound(null), 2600);
    return () => clearTimeout(t);
  }, [found]);

  /** Scroll the asked-for message into the middle of the pane. True if it was there. */
  const landOn = (el, id) => {
    const target = Array.from(el.querySelectorAll('.ch-msg[data-id]'))
      .find((n) => n.getAttribute('data-id') === String(id));
    if (!target) return false;
    el.scrollTop = Math.max(0, target.offsetTop - el.clientHeight / 2 + target.offsetHeight / 2);
    pinned.current = nearBottom(el);
    setFound(String(id));
    return true;
  };

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const last = messages.length > 0 ? messages[messages.length - 1] : null;
    // An older page has landed above what was on screen: keep that in place.
    if (anchor.current && anchor.current.place === place) {
      const row = el.querySelector(`.ch-msg[data-id="${CSS.escape(anchor.current.id)}"]`);
      if (row && messages[0] && String(messages[0].id) !== anchor.current.id) {
        el.scrollTop += (row.getBoundingClientRect().top - el.getBoundingClientRect().top) - anchor.current.top;
        anchor.current = null;
      } else if (!loadingOlder) {
        // Came back with nothing new (or failed): nothing to hold in place.
        anchor.current = null;
      }
    }
    const wanted = focusId ? `${place}|${focusId}|${focusNonce}` : null;
    if (wanted && focused.current !== wanted && ready
      && messages.some((m) => String(m.id) === String(focusId))) {
      if (landOn(el, focusId)) {
        focused.current = wanted;
        landed.current = place;
        lastKey.current = last ? last.key : null;
        return;
      }
    }
    if (landed.current !== place) {
      if (!ready || unreadKey === undefined) return;
      landed.current = place;
      lastKey.current = last ? last.key : null;
      const line = unreadKey != null ? el.querySelector('.ch-unread-line') : null;
      // The line a little below the top, so the last thing already read is
      // still just in view above it - that is what tells you where you were.
      el.scrollTop = line ? Math.max(0, line.offsetTop - 40) : el.scrollHeight;
      pinned.current = nearBottom(el);
      return;
    }
    const key = last ? last.key : null;
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (pinned.current || last?.mine) {
      el.scrollTop = el.scrollHeight;
      pinned.current = true;
      setJump(false);
    } else {
      setJump(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, messages, place, ready, unreadKey, focusId, focusNonce, loadingOlder]);

  /*
   * A screenshot's preview arrives after the line it is in has been drawn,
   * and makes that line taller. If you were at the bottom, you stay there,
   * rather than the conversation ending a picture's height below the screen.
   * Listened for natively, in the capture phase, because `load` does not
   * bubble.
   */
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const settle = () => { if (pinned.current) el.scrollTop = el.scrollHeight; };
    el.addEventListener('load', settle, true);
    return () => el.removeEventListener('load', settle, true);
  }, []);

  /** Ask for the page above, remembering where the reader is (anchor, above). */
  const older = () => {
    const el = scroller.current;
    if (!el || !onLoadOlder || !hasOlder || loadingOlder) return;
    const first = el.querySelector('.ch-msg[data-id]');
    anchor.current = first ? {
      place,
      id: first.getAttribute('data-id'),
      top: first.getBoundingClientRect().top - el.getBoundingClientRect().top
    } : null;
    onLoadOlder();
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    pinned.current = nearBottom(el);
    if (pinned.current && jump) setJump(false);
    // Scrolled up to near the top, once the conversation has landed: the
    // next page comes on its own, as it does in Slack or Teams.
    if (el.scrollTop < 160 && hasOlder && !loadingOlder && landed.current === place) older();
  };

  const goDown = () => {
    const el = scroller.current;
    if (!el) return;
    pinned.current = true;
    setJump(false);
    el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  const drawRow = (row) => {
    if (row.type === 'divider') {
      return (
        <div className="ch-branch-divider" key={row.key} role="separator" aria-label={row.label}>
          <span>{row.label}</span>
        </div>
      );
    }
    if (row.type === 'unread') {
      return (
        <div className="ch-unread-line" key={row.key} role="separator"
          aria-label="New messages">
          <span>New messages</span>
        </div>
      );
    }
    if (row.type === 'note') {
      const { m } = row;
      // 096: "Marked answered by Rita Borg · 14:02" - the whole line is the
      // body, its time already in it (Malta time, lib/when), so no clock.
      if (m.state) return <p className="ch-note ch-note-state" key={row.key} data-state="true">{m.body}</p>;
      // Who joined, who left, what it was renamed to - said small and in the
      // middle, so it reads as something that happened rather than something
      // somebody said.
      return (
        <p className="ch-note" key={row.key}>
          <span className="ch-note-who">{m.mine ? 'You' : m.author}</span>
          {' '}{m.body}
          <span className="ch-note-at">{clockOf(m.at)}</span>
        </p>
      );
    }
    return (
      <Row key={row.key} row={row} onFileError={onFileError} react={react}
        found={found !== null && String(row.m.id) === found} />
    );
  };

  const days = [];
  for (const row of rows) {
    if (row.type === 'day') {
      days.push({ key: row.key, label: row.label, rows: [] });
    } else {
      if (days.length === 0) days.push({ key: 'undated', label: null, rows: [] });
      days[days.length - 1].rows.push(row);
    }
  }

  return (
    <div className="ch-timeline">
      <div className="ch-messages" ref={scroller} onScroll={onScroll} aria-busy={loadingOlder || undefined}>
        {hasOlder && onLoadOlder && messages.length > 0 && (
          <div className="ch-older">
            <button type="button" className="ch-older-btn" onClick={older} disabled={loadingOlder}>
              {loadingOlder ? 'Loading older messages…' : 'Load older messages'}
            </button>
          </div>
        )}
        {messages.length === 0 && empty}
        {days.map((d) => (
          // ONE BLOCK PER DAY, so that day's label sticks to the top only
          // while you are scrolling through that day, and the next day's label
          // pushes it off - rather than every label piling up at the top.
          <div className="ch-day-group" key={d.key}>
            {d.label && (
              <div className="ch-day" role="separator" aria-label={d.label}>
                <span className="ch-day-label">{d.label}</span>
              </div>
            )}
            {d.rows.map(drawRow)}
          </div>
        ))}
      </div>
      {jump && (
        <button type="button" className="ch-jump" onClick={goDown}>
          <span aria-hidden="true">↓</span> New messages
        </button>
      )}
      {sheetFor && react && (
        <ReactionSheet
          anchor={sheetFor.anchor}
          onPick={canReact(sheetFor.m, react)
            ? (emoji) => { const { m } = sheetFor; setSheetFor(null); react.onReact(m, emoji); } : null}
          // The whole picker opens by the smiley that opened THIS, not by
          // the "More" button, which is gone by the time it is placed.
          onMore={(more) => { const { m, anchor } = sheetFor; setSheetFor(null); react.onMore(m, anchor || more); }}
          onTodo={canTodo(sheetFor.m, react) ? () => { const { m } = sheetFor; setSheetFor(null); react.onTodo(m); } : null}
          onBranch={canBranch(sheetFor.m, react) ? () => { const { m } = sheetFor; setSheetFor(null); react.onBranch(m); } : null}
          onEdit={canChange(sheetFor.m, react) && sheetFor.m.mayEdit && react.onEdit
            ? () => { const { m } = sheetFor; setSheetFor(null); react.onEdit(m); } : null}
          onDelete={canChange(sheetFor.m, react) && sheetFor.m.mayDelete && react.onDelete
            ? () => { const { m } = sheetFor; setSheetFor(null); setConfirmFor(m); } : null}
          onClose={() => setSheetFor(null)}
        />
      )}
      {versionsOf && (
        <Versions m={versionsOf.m} anchor={versionsOf.anchor} onClose={() => setVersionsOf(null)} />
      )}
      {confirmFor && react?.onDelete && (
        <DeleteConfirm m={confirmFor} onConfirm={() => { const m = confirmFor; setConfirmFor(null); react.onDelete(m); }}
          onClose={() => setConfirmFor(null)} />
      )}
    </div>
  );
}

export default ChatTimeline;
